/**
 * Community Schema
 * Zod schema for community creation and validation
 */

import { z } from "zod"

import {
  facebookUrlValidator,
  instagramUrlValidator,
  twitterUrlValidator,
  websiteUrlValidator,
} from "@/lib/validators/url.validator"

/**
 * Community Schema
 * Comprehensive validation for all community form fields
 */
export const createCommunitySchema = z.object({
  // Community Info fields
  name: z
    .string()
    .min(1, "Community name is required.")
    .max(100, "Name must not exceed 100 characters"),
  handle: z
    .string()
    .min(1, "Community handle is required.")
    .max(50, "Handle must not exceed 50 characters")
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      "Handle can only contain letters, numbers, hyphens, and underscores",
    ),
  type: z.enum(["public", "private"]),
  isOrganization: z.boolean(),
  address: z.string().optional(),

  // Description fields
  description: z
    .string()
    .min(1, "Description is required.")
    .max(2000, "Description must not exceed 2000 characters"),
  websiteUrl: websiteUrlValidator,
  instagramUrl: instagramUrlValidator,
  facebookUrl: facebookUrlValidator,
  twitterUrl: twitterUrlValidator,

  // Guidelines fields
  guidelines: z
    .string()
    .min(1, "Community guidelines are required.")
    .max(5000, "Guidelines must not exceed 5000 characters"),

  // Tags
  tags: z.array(z.string()).min(1, "Add at least one tag").max(10, "Maximum 10 tags allowed"),

  // Privacy settings
  privacySettings: z.object({
    addToRituals: z.array(z.string()).min(1, "Select who can add/accept rituals to community"),
    comment: z.array(z.string()).min(1, "Select who can comment"),
  }),

  // Selected users (required - at least one member)
  selectedUsers: z
    .array(
      z.object({
        userId: z.string(),
        name: z.string().nullable().optional(),
        email: z.string().optional(),
        username: z.string().nullable().optional(),
        profilePictureUrl: z.string().nullable().optional(),
        roleIds: z.array(z.string()).min(1, "Select at least one role"),
        roleId: z.string().optional(),
        communityBuilder: z.boolean().optional(),
        isCurrentUser: z.boolean().optional(),
      }),
    )
    .min(1, "Add at least one community member"),
})

/**
 * Export form data type
 */
export type CreateCommunityFormData = z.infer<typeof createCommunitySchema>
